<template>
    <div>
        <ViewHeader>
            <ViewTitle>
                {{ i18n("voucherDesign.voucherDesign") }}
                <small></small>
            </ViewTitle>

            <NavTabs>
                <NavTab
                    :title="i18n('voucherDesign.edit')"
                    href="#voucher-design-edit"
                    icon="flaticon-edit"
                    :is-active="true"
                />
                <NavTab
                    :title="i18n('voucherDesign.preview')"
                    href="#voucher-design-preview"
                    icon="flaticon-eye"
                    :is-active="false"
                />
            </NavTabs>
        </ViewHeader>

        <TabContent>
            <TabPanel id="voucher-design-edit" :is-active="true">
                <div class="form-group row" style="align-items: center; justify-content: center">
                    <VoucherHtmlEditor
                        ref="htmlEditor"
                        v-model="documentContent"
                        :locationId="currentLocationId"
                        :locationDetail="locationDetail"
                    />
                </div>
            </TabPanel>
            <TabPanel id="voucher-design-preview" :is-active="false">
                <div class="form-group row" style="align-items: center; justify-content: center">
                    <div class="html-editor-preview-container">
                        <div class="html-editor-preview" v-html="documentContent"></div>
                    </div>
                </div>
            </TabPanel>
        </TabContent>
    </div>
</template>

<script setup lang="ts">
    import type { IUpdateVoucherDesignCmd } from "@/data/commands/location/UpdateVoucherDesignCmd";
    import { Factory } from "@/data/dao/Factory";
    import type { DaoError } from "@/data/dao/service/BaseServiceDao";
    import { LocationDao } from "@/data/dao/service/LocationDao";
    import type { ILocationDetail, IVoucherDesign } from "@/data/model/Location";
    import { i18n } from "@/portal/Trans";
    import { UiNotification } from "@/portal/ui/Notification";
    import { translations } from "@/shared/localization/translations";
    import { logError } from "@/shared/logging/errors";
    import { computed, onMounted, ref, watch } from "vue";

    import VoucherHtmlEditor from "./editor/VoucherHtmlEditor.vue";
    import NavTab from "@/portal/components/layout/details/NavTab.vue";
    import NavTabs from "@/portal/components/layout/details/NavTabs.vue";
    import TabContent from "@/portal/components/layout/details/TabContent.vue";
    import TabPanel from "@/portal/components/layout/details/TabPanel.vue";
    import ViewHeader from "@/portal/components/layout/details/ViewHeader.vue";
    import ViewTitle from "@/portal/components/layout/details/ViewTitle.vue";

    const locationDao = Factory.getInstance<LocationDao>(LocationDao);

    const isInitialized = ref<boolean>(false);
    const htmlEditor = ref<InstanceType<typeof VoucherHtmlEditor>>();
    const documentContent = ref<string>("");
    const currentLocationId = ref<string>("");
    const locationDetail = ref<ILocationDetail>();
    const originalTemplate = ref<string>("");
    const isTemplateChanged = computed(() => documentContent.value !== originalTemplate.value);

    const props = defineProps<{
        removeVoucherTemplateBtn: JQuery;
    }>();

    async function setFormData(
        voucherDesign: IVoucherDesign | undefined,
        locationId: string,
        model: ILocationDetail | undefined
    ) {
        currentLocationId.value = locationId;
        locationDetail.value = model;
        isInitialized.value = true;
        originalTemplate.value = voucherDesign?.htmlTemplate ?? "";
        htmlEditor.value?.updateModel(originalTemplate.value);
    }

    function validate() {
        return htmlEditor.value?.validateRequiredVariablesAndImages() ?? false;
    }

    function getData(): IUpdateVoucherDesignCmd | undefined {
        if (!validate()) {
            return undefined;
        }

        return {
            htmlTemplate: documentContent.value,
        };
    }

    function resolveErrorMessage(e, b: DaoError): string | undefined {
        if (b?.additionalData?.reason == null) {
            return undefined;
        }

        return b.additionalData.reason;
    }

    const shouldValidateLeaving = ref<boolean>(true);
    onMounted(() => {
        const $tabs = $("#locationportlet > .kt-portlet__head .nav-tabs");

        $tabs.on("show.bs.tab", (b) => {
            const isVisible = $("#voucher-design").is(".active");
            const target = $(b.target);

            if (isVisible && isTemplateChanged.value && shouldValidateLeaving.value) {
                b.preventDefault();
                b.stopImmediatePropagation();

                UiNotification.confirmWithOptions({
                    message: i18n("voucherDesign.areYouSureYouWantToLeave"),
                    confirmBtn: translations.general.ok,
                    cancelBtn: translations.general.cancel,
                    confirmed: () => {
                        shouldValidateLeaving.value = false;
                        target.click();
                        shouldValidateLeaving.value = true;
                    },
                });
            }
        });

        props.removeVoucherTemplateBtn.on("click", (e) => {
            e.preventDefault();
            e.stopPropagation();

            UiNotification.confirmWithOptions({
                message: i18n("voucherDesign.areYouSureYouWantToRemoveTemplate"),
                confirmBtn: translations.general.ok,
                cancelBtn: translations.general.cancel,
                confirmed: async () => {
                    try {
                        const newLocationDetail = await locationDao.setSelectedVoucherDesign(currentLocationId.value, {
                            useDefaultTemplate: true,
                        });
                        locationDetail.value = newLocationDetail;
                        isInitialized.value = true;
                        originalTemplate.value = newLocationDetail.voucherDesign?.htmlTemplate ?? "";
                        htmlEditor.value?.updateModel(originalTemplate.value);
                    } catch (ex) {
                        UiNotification.showException(i18n("voucherDesign.errorRemovingTemplate"));
                        logError(ex);
                    }
                },
            });
        });
    });

    watch(originalTemplate, () => {
        if (originalTemplate.value == null || originalTemplate.value === "") {
            props.removeVoucherTemplateBtn.hide();
        } else {
            props.removeVoucherTemplateBtn.show();
        }
    });

    defineExpose({
        validate,
        getData,
        setFormData,
        resolveErrorMessage,
    });
</script>

<style lang="scss">
    @use "./../../../../assets/styles/scss/shared/colors.scss";

    .voucher-design-form {
        padding-top: 14px !important;
        padding-bottom: 0 !important;

        .kt-portlet__head.kt-portlet__head--lg {
            min-height: auto;
            margin-bottom: 21px;
        }

        .html-editor-preview-container {
            box-sizing: content-box;
            border: 1px solid #ccc;
        }
    }

    .delete-voucher-template-btn {
        color: colors.$red !important;
        font-size: 13px;
        line-height: 40px;
    }
</style>
